import { Injectable, inject } from '@angular/core';
import { Observable, EMPTY, of, from, firstValueFrom } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';
import { AuthStateService } from './auth-state.service';
import { EncryptionService } from './encryption.service';
import { DecryptedUserPermissions, DecryptedIconSettings, DecryptedOtherSettings } from '../models/user-permissions.model';
import { Tenant } from '../models/tenant.model';
// 89dcf91 — mfa skipped for non hosted dev practice (disabled)
// import { environment } from '../../environments/environment';
import {
  MFA_PENDING_AUDIT_EVENTS_KEY,
  PendingMfaAuditEvent,
  PendingMfaLogin
} from '../models/patient-mfa.model';
import { persistDmEmail, resolvePostLoginRedirectRoute } from '../utils/book-appointment-settings.util';
import { resolveApiBusinessFailureMessage } from '../utils/http-connection-error.util';

export interface LoginResult {
  success: boolean;
  userInfo?: {
    userName: string;
    sessionId: string;
    patientId: string;
    prid: string;
    patientName: string;
    patientGender: string;
    patientDOB: string;
    representativeName?: string;
    dmEmail?: string;
  };
  hasAssociatedPatients: boolean;
  isQuickActivated: boolean;
  navigationRoute: string;
  successMessage?: string;
  errorMessage?: string;
  /** True when API returns validationMsg decrypting to "userselfblocked" (too many failed attempts). */
  isUserSelfBlocked?: boolean;
  /** Set when validationMsg is InvalidPassword2, 3, 4, or 5 (attempt number before account gets locked). */
  invalidPasswordAttempt?: number;
  /** Login credentials OK; complete session after MFA OTP on /patient-mfa. */
  requiresMfa?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  /** Set when MFA-unavailable flow opens profile; cleared after PortalSendIP runs on profile load. */
  private static readonly MFA_UNAVAILABLE_PORTAL_SEND_IP_FLAG = 'mfa_unavailable_portal_send_ip';

  private authService = inject(AuthService);
  private apiService = inject(ApiService);
  private storageService = inject(StorageService);
  private authStateService = inject(AuthStateService);
  private encryptionService = inject(EncryptionService);

  /**
   * Process login response and handle all session setup
   * @param loginResponse The response from getUserLogin API
   * @param tenantCode The tenant code for storing
   * @param currentTenant The current tenant object (optional, for navigation)
   * @returns Observable<LoginResult> with navigation and user info
   */
  processLoginResponse(
    loginResponse: any,
    tenantCode: string,
    currentTenant?: Tenant | null
  ): Observable<LoginResult> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // PE-6155 — HTTP 200 + success:"false" → safe user message (GENERIC → connection copy)
    if (!loginResponse || loginResponse?.success !== 'true' || !loginResponse?.data || loginResponse?.data?.length === 0) {
      return {
          success: false,
          hasAssociatedPatients: false,
          isQuickActivated: false,
          navigationRoute: '',
          errorMessage: resolveApiBusinessFailureMessage(
            loginResponse,
            'Login failed. Please contact your practice.'
          ),
        };
    }

    try {
      // Extract and decrypt authentication data from response
      const encryptedAuthData = loginResponse.data[0];
      const validationMsg = await this.encryptionService.decryptAes256Gcm(encryptedAuthData?.validationMsg || '');
      if (validationMsg === 'userselfblocked') {
        return {
            success: false,
            hasAssociatedPatients: false,
            isQuickActivated: false,
            navigationRoute: '',
            errorMessage: 'Too Many Failed Attempts',
            isUserSelfBlocked: true
          };
      }
      const invalidAttemptMatch = validationMsg?.match(/^InvalidPassword(\d+)$/);
      if (invalidAttemptMatch) {
        const attemptNumber = parseInt(invalidAttemptMatch[1], 10);
        return {
            success: false,
            hasAssociatedPatients: false,
            isQuickActivated: false,
            navigationRoute: '',
            invalidPasswordAttempt: attemptNumber
          };
      }
      const responseData = loginResponse.data[3];
      
      const firstName = await this.encryptionService.decryptAes256Gcm(responseData?.DMFirstName || '');
      const lastName = await this.encryptionService.decryptAes256Gcm(responseData?.DMLastName || '');
      const patientName = firstName + ' ' + lastName;
      
      // Extract representative name from responseData (DMFirstName + DMLastName)
      const representativeName = (firstName + ' ' + lastName).trim();
      
      const patientGender = await this.encryptionService.decryptAes256Gcm(responseData?.DMGender || '');
      const patientDOB = await this.encryptionService.decryptAes256Gcm(responseData?.DMDOB || '');
      const dmEmail = responseData?.DMEmail
        ? await this.encryptionService.decryptAes256Gcm(responseData.DMEmail)
        : '';
      
      // Decrypt the response fields
      const authData = {
        UserName: await this.encryptionService.decryptAes256Gcm(encryptedAuthData.UserName || ''),
        SessionId: await this.encryptionService.decryptAes256Gcm(encryptedAuthData.SessionId || ''),
        nUserID: await this.encryptionService.decryptAes256Gcm(encryptedAuthData.nUserID || ''),
        validationMsg: await this.encryptionService.decryptAes256Gcm(encryptedAuthData.validationMsg || '')
      };
      
      const isQuickActivated = authData.SessionId === "0" && authData.validationMsg === "QuickActivated";
      
      // Check if login was successful (skip validation for QuickActivated)
      if (!isQuickActivated && (!authData || authData.SessionId === "0" || authData.validationMsg !== "Success")) {
        return {
            success: false,
            hasAssociatedPatients: false,
            isQuickActivated: false,
            navigationRoute: '',
            errorMessage: 'Invalid username or password. Please try again.'
          };
      }
      
      // Check if user is a patient representative (has associated patients)
      const hasAssociatedPatients = encryptedAuthData?.gloPatientRepresentativeAssociatedPatientsField;
      
      // PatientChartNumber (cleargagepatientid) from data[3] for paybill API
      const patientChartNumber = responseData?.PatientChartNumber
        ? await this.encryptionService.decryptAes256Gcm(responseData.PatientChartNumber)
        : '';

      // Extract tenantName from responseData if available (check both tenantName and PracticeName)
      const tenantNameFromResponse = responseData?.tenantName
        ? await this.encryptionService.decryptAes256Gcm(responseData.tenantName)
        : (responseData?.PracticeName ? await this.encryptionService.decryptAes256Gcm(responseData.PracticeName) : '');

      // Create user info object
      const userInfo = {
        userName: authData.UserName,
        sessionId: authData.SessionId,
        patientId: authData.nUserID,
        prid: authData.nUserID,
        patientName: patientName,
        patientGender: patientGender,
        patientDOB: patientDOB,
        representativeName: hasAssociatedPatients ? representativeName : undefined,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        patientChartNumber: patientChartNumber,
        tenantName: tenantNameFromResponse,
        dmEmail: dmEmail.trim()
      };

      // Decrypt tenantId from response
      const decryptedTenantId = loginResponse?.tenantId ? await this.encryptionService.decryptAes256Gcm(loginResponse.tenantId) : '';

      // Store tenant info in session for paybill payload and API URL resolution (when currentTenant is available)
      if (currentTenant?.tenantName) {
        // Store unencrypted so it survives session-key changes (pre-login -> post-login)
        sessionStorage.setItem('tenant_name', currentTenant.tenantName);
      }
      // Store tenant_type directly in sessionStorage (unencrypted) for API URL resolution after page refresh
      // This is stored separately from encrypted data since TenantService needs to read it without StorageService
      if (currentTenant?.tenantType !== undefined) {
        sessionStorage.setItem('tenant_type', String(currentTenant.tenantType));
      }

      // Extract and decrypt module settings
      let decryptedPermissions: DecryptedUserPermissions | null = null;
      if (loginResponse.modulesetting && Array.isArray(loginResponse.modulesetting)) {
        decryptedPermissions = await this.decryptModuleSettings(loginResponse.modulesetting);
      }
      
      // Extract and decrypt icon settings
      let decryptedIconSettings: DecryptedIconSettings | null = null;
      if (loginResponse.iconsetting && Array.isArray(loginResponse.iconsetting)) {
        decryptedIconSettings = await this.decryptIconSettings(loginResponse.iconsetting);
      }
      
      // Extract and decrypt other settings
      let decryptedOtherSettings: DecryptedOtherSettings | null = null;
      if (loginResponse.othersetting && Array.isArray(loginResponse.othersetting)) {
        decryptedOtherSettings = await this.decryptOtherSettings(loginResponse.othersetting);
      }
      
      // PE-6129 — MFA only when modulesetting has mfa and shouldHide=false (opt-in)
      // Standard patient / patient representative login: MFA before persisting user_info
      if (!isQuickActivated && this.isTenantMfaModuleEnabled(decryptedPermissions)) {
        const isPatientRepresentative = !!hasAssociatedPatients;
        const associatedPatientsRaw = isPatientRepresentative
          ? (Array.isArray(hasAssociatedPatients) ? hasAssociatedPatients : [hasAssociatedPatients])
          : null;

        if (userInfo.sessionId) {
          this.storageService.setSessionId(userInfo.sessionId);
        }
        if (currentTenant?.tenantName) {
          sessionStorage.setItem('tenant_name', currentTenant.tenantName);
        }
        if (currentTenant?.tenantType !== undefined) {
          sessionStorage.setItem('tenant_type', String(currentTenant.tenantType));
        }
        this.authService.rememberTenantShortNameForSignIn(currentTenant?.tenantShortName);
        const mfaSubjectId = userInfo.prid || userInfo.patientId;
        this.storageService.setSessionStorage('pending_mfa_login', {
          loginResponse,
          tenantCode,
          currentTenant,
          userInfo,
          decryptedTenantId,
          hasAssociatedPatients: isPatientRepresentative,
          associatedPatientsRaw,
          npatientid: mfaSubjectId,
          decryptedPermissions,
          decryptedIconSettings,
          decryptedOtherSettings,
          isQuickActivated: false,
          autoSendPreferredMfa: false,
          preferredMfaChannel: null
        });
        // PE-6337 — flatten Observable MFA preference path inside async processLoginResponse
        return await firstValueFrom(
          this.authService.getPatientMfaPreference(mfaSubjectId, tenantCode).pipe(
            switchMap((pref) => {
              const pending = (this.storageService.getSessionStorage('pending_mfa_login') ??
                {}) as PendingMfaLogin;
              const autoSendPreferredMfa =
                pref?.status === 'success' && !!pref.mfaEnabled && !!pref.preferredChannel;
              const updatedPending: PendingMfaLogin = {
                ...pending,
                mfaPreference: pref?.status === 'success' ? pref : null,
                autoSendPreferredMfa,
                preferredMfaChannel:
                  autoSendPreferredMfa && pref?.preferredChannel
                    ? String(pref.preferredChannel)
                    : null
              };
              if (pref?.status === 'success' && pref.introCompleted && !pref.mfaEnabled) {
                return this.completeLoginWithoutMfa();
              }
              this.storageService.setSessionStorage('pending_mfa_login', updatedPending);
              return of({
                success: true,
                requiresMfa: true,
                userInfo,
                hasAssociatedPatients: false,
                isQuickActivated: false,
                navigationRoute: '/patient-mfa'
              } as LoginResult);
            }),
            catchError(() => of({
              success: true,
              requiresMfa: true,
              userInfo,
              hasAssociatedPatients: isPatientRepresentative,
              isQuickActivated: false,
              navigationRoute: '/patient-mfa'
            } as LoginResult))
          )
        );
      }

      // Store sessionId separately (unencrypted) to derive encryption key
      if (userInfo.sessionId) {
        this.storageService.setSessionId(userInfo.sessionId);
      }
      
      // Store user info in encrypted sessionStorage for persistence
      this.storageService.setSessionStorage('user_info', userInfo);
      this.storageService.setSessionStorage('tenant_id', decryptedTenantId);
      this.storageService.setSessionStorage('tenant_code', tenantCode);
      this.storageService.setSessionStorage('is_quick_activated', isQuickActivated);
      // Persist `dm_email` for appointment confirmation toperson
      this.persistDmEmailForUser(userInfo);
      this.authService.rememberTenantShortNameForSignIn(currentTenant?.tenantShortName);
      
      // Store decrypted permissions
      if (decryptedPermissions) {
        this.storageService.setSessionStorage('user_permissions', decryptedPermissions);
      }
      
      // Store decrypted icon settings
      if (decryptedIconSettings) {
        this.storageService.setSessionStorage('user_icon_settings', decryptedIconSettings);
      }
      
      // Store decrypted other settings
      if (decryptedOtherSettings) {
        this.storageService.setSessionStorage('user_other_settings', decryptedOtherSettings);
      }
      
      this.authStateService.setLoggedIn(true, userInfo);
      
      // Preload home route chunk immediately after login (if navigating to home)
      if (!hasAssociatedPatients && !isQuickActivated) {
        import('../pages/home/home.component').catch(() => {
          // Silently fail - chunk will load during navigation anyway
        });
      }
      
      // Call PortalSendIP API after successful login (for audit logs) - fire and forget
      if (!isQuickActivated) {
        const patientUID = hasAssociatedPatients ? userInfo.prid : userInfo.patientId;
        const prid = userInfo.prid;
        
        this.authService.portalSendIP(
          patientUID,
          prid,
          "",
          decryptedTenantId
        ).pipe(
          catchError((error: any) => {
            // Log error but don't block login flow
            console.error('PortalSendIP API call failed:', error);
            return EMPTY;
          })
        ).subscribe();
      }
      
      // Determine navigation route
      let navigationRoute: string;
      if (hasAssociatedPatients) {
        // Store associated patients data
        const associatedPatients = Array.isArray(hasAssociatedPatients) 
          ? hasAssociatedPatients 
          : [hasAssociatedPatients];
        this.storageService.setSessionStorage('associated_patients', associatedPatients);
        navigationRoute = '/associatedPatients';
      } else {
        if (isQuickActivated) {
          navigationRoute = '/quick-activate';
        } else {
          navigationRoute = resolvePostLoginRedirectRoute('/home', decryptedPermissions);
        }
      }
      
      // Determine success message
      let successMessage: string | undefined;
      if (hasAssociatedPatients) {
        successMessage = userInfo.patientName;
      } else if (isQuickActivated) {
        successMessage = 'Please complete your account setup';
      } else {
        successMessage = userInfo.patientName;
      }
      
      return {
          success: true,
          userInfo: userInfo,
          hasAssociatedPatients: !!hasAssociatedPatients,
          isQuickActivated: isQuickActivated,
          navigationRoute: navigationRoute,
          successMessage: successMessage
        };
    } catch (error) {
      return {
          success: false,
          hasAssociatedPatients: false,
          isQuickActivated: false,
          navigationRoute: '',
          errorMessage: 'Error processing login response. Please try again.'
        };
    }
  
    })());
  }

  /**
   * 89dcf91 — dev-only: skip MFA when tenant uses apiUrlNonHosted (tenantType === 1)
   * and short name is listed in environment.skipMfaNonHostedTenantShortNames.
   */
  // shouldSkipMfa(currentTenant?: Tenant | null): boolean {
  //   const skipNames = environment.skipMfaNonHostedTenantShortNames;
  //   if (!skipNames?.length || !environment.apiUrlNonHosted) {
  //     return false;
  //   }
  //   if (currentTenant?.tenantType !== 1) {
  //     return false;
  //   }
  //   const shortName = currentTenant.tenantShortName?.trim();
  //   return !!shortName && skipNames.includes(shortName);
  // }

  /**
   * MFA contacts unavailable — complete portal session so the patient can open profile,
   * cancel without updating, and use other modules (access log, etc.) normally.
   */
  establishSessionForMfaProfileUpdate(): boolean {
    const pending = this.storageService.getSessionStorage('pending_mfa_login');
    if (!pending?.userInfo) {
      return false;
    }

    const userInfo = pending.userInfo;
    const tenantCode = pending.tenantCode || '';
    const decryptedTenantId = pending.decryptedTenantId || '';
    const isQuickActivated = !!pending.isQuickActivated;
    const hasAssociatedPatients = !!pending.hasAssociatedPatients;
    const currentTenant = pending.currentTenant as Tenant | null | undefined;

    if (userInfo.sessionId) {
      this.storageService.setSessionId(userInfo.sessionId);
    }
    this.storageService.setSessionStorage('user_info', userInfo);
    this.storageService.setSessionStorage('tenant_id', decryptedTenantId);
    this.storageService.setSessionStorage('tenant_code', tenantCode);
    this.storageService.setSessionStorage('is_quick_activated', isQuickActivated);
    // Persist `dm_email` for appointment confirmation toperson
    this.persistDmEmailForUser(userInfo);
    this.authService.rememberTenantShortNameForSignIn(currentTenant?.tenantShortName);

    if (currentTenant?.tenantName) {
      sessionStorage.setItem('tenant_name', currentTenant.tenantName);
    }
    if (currentTenant?.tenantType !== undefined) {
      sessionStorage.setItem('tenant_type', String(currentTenant.tenantType));
    }

    if (pending.decryptedPermissions) {
      this.storageService.setSessionStorage('user_permissions', pending.decryptedPermissions);
    }
    if (pending.decryptedIconSettings) {
      this.storageService.setSessionStorage('user_icon_settings', pending.decryptedIconSettings);
    }
    if (pending.decryptedOtherSettings) {
      this.storageService.setSessionStorage('user_other_settings', pending.decryptedOtherSettings);
    }

    if (hasAssociatedPatients && pending.associatedPatientsRaw) {
      this.storageService.setSessionStorage('associated_patients', pending.associatedPatientsRaw);
    }

    this.storageService.removeSessionStorageByKey('pending_mfa_login');

    this.authStateService.setLoggedIn(true, userInfo);

    if (!hasAssociatedPatients && !isQuickActivated) {
      import('../pages/home/home.component').catch(() => undefined);
    }

    return true;
  }

  /**
   * MFA unavailable only — complete portal session and defer audit log until profile loads.
   */
  finalizeMfaUnavailablePortalAccess(): boolean {
    if (!this.establishSessionForMfaProfileUpdate()) {
      return false;
    }
    sessionStorage.setItem(LoginService.MFA_UNAVAILABLE_PORTAL_SEND_IP_FLAG, '1');
    return true;
  }

  /**
   * MFA unavailable only — run PortalSendIP once after profile page load (avoids abort on reload).
   */
  recordPendingMfaUnavailablePortalAccess(): void {
    if (sessionStorage.getItem(LoginService.MFA_UNAVAILABLE_PORTAL_SEND_IP_FLAG) !== '1') {
      return;
    }
    sessionStorage.removeItem(LoginService.MFA_UNAVAILABLE_PORTAL_SEND_IP_FLAG);

    const userInfo = this.storageService.getSessionStorage('user_info');
    const tenantId = this.storageService.getSessionStorage('tenant_id') || '';
    const isQuickActivated = this.storageService.getSessionStorage('is_quick_activated') === true;
    const associatedPatients = this.storageService.getSessionStorage('associated_patients');
    const hasAssociatedPatients = Array.isArray(associatedPatients) && associatedPatients.length > 0;

    if (isQuickActivated || !userInfo?.sessionId) {
      return;
    }

    const patientUID = hasAssociatedPatients ? userInfo.prid : userInfo.patientId;
    this.authService.portalSendIP(patientUID, userInfo.prid, '', tenantId).pipe(
      catchError(() => EMPTY)
    ).subscribe();
  }

  /**
   * After MFA validate-otp succeeds; finishes session setup from pending_mfa_login.
   */

  completeLoginAfterMfa(): Observable<LoginResult> {
    const pending = this.storageService.getSessionStorage('pending_mfa_login');
    if (!pending?.userInfo) {
      return new Observable(observer => {
        observer.next({
          success: false,
          hasAssociatedPatients: false,
          isQuickActivated: false,
          navigationRoute: '',
          errorMessage: 'Session expired. Please sign in again.'
        });
        observer.complete();
      });
    }

    this.storageService.removeSessionStorageByKey('pending_mfa_login');

    const userInfo = pending.userInfo;
    const tenantCode = pending.tenantCode || '';
    const decryptedTenantId = pending.decryptedTenantId || '';
    const isQuickActivated = !!pending.isQuickActivated;
    const hasAssociatedPatients = !!pending.hasAssociatedPatients;

    if (userInfo.sessionId) {
      this.storageService.setSessionId(userInfo.sessionId);
    }
    this.storageService.setSessionStorage('user_info', userInfo);
    this.storageService.setSessionStorage('tenant_id', decryptedTenantId);
    this.storageService.setSessionStorage('tenant_code', tenantCode);
    this.storageService.setSessionStorage('is_quick_activated', isQuickActivated);
    // Persist `dm_email` for appointment confirmation toperson
    this.persistDmEmailForUser(userInfo);
    this.authService.rememberTenantShortNameForSignIn(pending.currentTenant?.tenantShortName);

    if (pending.decryptedPermissions) {
      this.storageService.setSessionStorage('user_permissions', pending.decryptedPermissions);
    }
    if (pending.decryptedIconSettings) {
      this.storageService.setSessionStorage('user_icon_settings', pending.decryptedIconSettings);
    }
    if (pending.decryptedOtherSettings) {
      this.storageService.setSessionStorage('user_other_settings', pending.decryptedOtherSettings);
    }

    this.authStateService.setLoggedIn(true, userInfo);

    if (!hasAssociatedPatients && !isQuickActivated) {
      import('../pages/home/home.component').catch(() => undefined);
    }

    if (!isQuickActivated) {
      const patientUID = hasAssociatedPatients ? userInfo.prid : userInfo.patientId;
      this.authService.portalSendIP(patientUID, userInfo.prid, '', decryptedTenantId).pipe(
        catchError(() => EMPTY),
        // PE-6224 — login MFA audits were queued before PortalSendIP; flush for direct patients.
        // Representatives flush on associated-patients select (Audit Log uses selected PatientUID).
        finalize(() => {
          if (!hasAssociatedPatients) {
            this.flushPendingMfaAuditEvents(userInfo.patientId, decryptedTenantId);
          }
        })
      ).subscribe();
    } else if (!hasAssociatedPatients) {
      this.flushPendingMfaAuditEvents(userInfo.patientId, decryptedTenantId);
    }

    let navigationRoute: string;
    if (hasAssociatedPatients) {
      const associatedPatients = pending.associatedPatientsRaw;
      if (associatedPatients) {
        this.storageService.setSessionStorage('associated_patients', associatedPatients);
      }
      navigationRoute = '/associatedPatients';
    } else if (isQuickActivated) {
      navigationRoute = '/quick-activate';
    } else {
      navigationRoute = resolvePostLoginRedirectRoute('/home', pending.decryptedPermissions);
    }

    let successMessage: string | undefined;
    if (hasAssociatedPatients) {
      successMessage = userInfo.patientName;
    } else if (isQuickActivated) {
      successMessage = 'Please complete your account setup';
    } else {
      successMessage = userInfo.patientName;
    }

    return new Observable(observer => {
      observer.next({
        success: true,
        userInfo,
        hasAssociatedPatients,
        isQuickActivated,
        navigationRoute,
        successMessage
      });
      observer.complete();
    });
  }

  /**
   * When MFA is disabled (introCompleted=true, mfaEnabled=false), finishes session setup from pending_mfa_login
   * without requiring OTP validation.
   */
  completeLoginWithoutMfa(): Observable<LoginResult> {
    return this.completeLoginAfterMfa();
  }

  /**
   * PE-6129 — tenant MFA is opt-in via modulesetting.
   * Enabled only when mfa row is present and shouldHide is false (permission.mfa === true).
   * Missing row, missing modulesetting, or shouldHide=true → skip MFA workflow.
   */
  isTenantMfaModuleEnabled(
    permissions: Pick<DecryptedUserPermissions, 'mfa'> | { mfa?: boolean } | null | undefined
  ): boolean {
    return permissions?.mfa === true;
  }

  /**
   * Helper method to decrypt module settings from modulesetting array
   * API Logic:
   * - Empty array [] = show all modules (all permissions = true)
   * - Non-empty array = check each module's settingvalue
   * - If settingvalue decrypts to "true", hide the module (set permission = false)
   * - If settingvalue decrypts to "false", show the module (keep permission = true)
   * - PE-6102 / PE-6129 — bookappointment and mfa are opt-in (default false when missing)
   */
  private async decryptModuleSettings(modulesetting: any[]): Promise<DecryptedUserPermissions> {
    const decrypted: any = {
      // Initialize all permissions to true by default (show all modules)
      healthinformationsharehistory: true,
      appointments: true,
      healthrecord: true,
      viewstatements: true,
      refillrequest: true,
      askprovider: true,
      patientforms: true,
      apptrequest: true,
      sent: true,
      accesslog: true,
      phi: true,
      paybills: true,
      askstaff: true,
      restrictionrequest: true,
      inbox: true,
      examnotes: true,
      // PE-6131 — Labs Results; missing modulesetting row = shown
      results: true,
      // PE-6102 — opt-in; missing modulesetting row = hidden
      bookappointment: false,
      // PE-6129 — opt-in; missing modulesetting row = skip MFA
      mfa: false,
      tenantid: ''
    };
    
    // Helper to decrypt setting name
    const decryptSettingName = async (encryptedName: string): Promise<string> => {
      if (!encryptedName) return '';
      try {
        return (await this.encryptionService.decryptAes256Gcm(encryptedName)).toLowerCase().trim();
      } catch (e) {
        console.warn('Failed to decrypt setting name:', encryptedName, e);
        return '';
      }
    };
    
    // Helper to decrypt and convert to boolean
    // Returns true only if decrypted value is exactly 'true' (case-insensitive)
    // Empty strings, 'false', or any other value returns false (meaning: show module)
    const decryptToBoolean = async (encryptedValue: string): Promise<boolean> => {
      if (!encryptedValue) return false; // Empty encrypted value → show module
      try {
        const decryptedValue = await this.encryptionService.decryptAes256Gcm(encryptedValue);
       // return decryptedValue.toLowerCase().trim() === 'true';
        const normalizedValue = decryptedValue.toLowerCase().trim();
        // Only 'true' means hide module, everything else (including '' and 'false') means show module
        return normalizedValue === 'true';
      } catch (e) {
        console.warn('Failed to decrypt permission value:', encryptedValue, e);
        return false; // On error, default to showing module
      }
    };
    
    // Mapping from decrypted setting names to permission keys
    const settingNameToPermissionMap: { [key: string]: keyof DecryptedUserPermissions } = {
      'healthrecord': 'healthrecord',
      'viewstatement': 'viewstatements',
      'refillrequest': 'refillrequest',
      'askaprovider': 'askprovider',
      'patientform': 'patientforms',
      'appointmentrequest': 'apptrequest',
      'appointments': 'appointments',
      'phi': 'phi',
      'accesslog': 'accesslog',
      'paybill': 'paybills',
      'askastaff': 'askstaff',
      'restrictionrequest': 'restrictionrequest',
      // PE-6040 — backend modulesetting settingname
      'examnotes': 'examnotes',
      // PE-6131 — Labs Results module toggle (modulesetting only)
      'results': 'results',
      // PE-6102 — self-scheduling module toggle (modulesetting only)
      'bookappointment': 'bookappointment',
      // PE-6129 — tenant MFA module toggle (modulesetting only)
      'mfa': 'mfa'
    };
    
    // Process each module setting
    // Module settings are INDEPENDENT from icon settings - they come from different backend arrays
    for (const setting of modulesetting) {
      // Skip if settingname or settingvalue is missing
      // Note: Empty string settingvalue is handled by decryptToBoolean (returns false = show module)
      if (!setting.settingname || !setting.settingvalue) {
        continue;
      }
      
      const decryptedSettingName = await decryptSettingName(setting.settingname);
      if (!decryptedSettingName) {
        continue;
      }
      
      const permissionKey = settingNameToPermissionMap[decryptedSettingName];
      if (permissionKey) {
        const shouldHide = await decryptToBoolean(setting.settingvalue);
        // PE-6102 / PE-6129 — BookAppointment and MFA are opt-in (default hidden/off)
        if (permissionKey === 'bookappointment' || permissionKey === 'mfa') {
          decrypted[permissionKey] = !shouldHide;
        } else if (shouldHide) {
          decrypted[permissionKey] = false;
        }
      }
    }
    
    // Set tenantid from the first setting if available
    if (modulesetting.length > 0 && modulesetting[0].tenantid) {
      try {
        decrypted.tenantid = await this.encryptionService.decryptAes256Gcm(modulesetting[0].tenantid);
      } catch (e) {
        console.warn('Failed to decrypt tenantid from modulesetting:', e);
        decrypted.tenantid = '';
      }
    }
    
    return decrypted as DecryptedUserPermissions;
  }
  
  /**
   * Decrypts each iconsetting object (settingname, settingvalue, tenantid).
   * After decrypt, `settingvalue` is expected to be `'true'` (hide) or `'false'` (show);
   */
  private async decryptIconSettings(iconsetting: any[]): Promise<DecryptedIconSettings> {
    if (!iconsetting || !Array.isArray(iconsetting)) {
      return [];
    }
    
    const decryptedSettings: DecryptedIconSettings = [];
    
    for (const setting of iconsetting) {
      if (!setting.settingname || !setting.settingvalue) {
        continue; // Skip invalid settings
      }

      try {
        const decryptedSetting = {
          settingname: await this.encryptionService.decryptAes256Gcm(setting.settingname),
          settingvalue: await this.encryptionService.decryptAes256Gcm(setting.settingvalue),
          tenantid: setting.tenantid ? await this.encryptionService.decryptAes256Gcm(setting.tenantid) : '',
          id: setting.id || ''
        };

        decryptedSettings.push(decryptedSetting);
      } catch (e) {
        // Skip this setting if decryption fails
      }
    }
    
    return decryptedSettings;
  }

  /**
   * Helper method to decrypt other settings from othersetting array
   * Decrypts settingname, settingvalue, tenantid, and preserves jsondata for each other setting
   */
  private async decryptOtherSettings(othersetting: any[]): Promise<DecryptedOtherSettings> {
    if (!othersetting || !Array.isArray(othersetting)) {
      return [];
    }
    
    const decryptedSettings: DecryptedOtherSettings = [];
    
    for (const setting of othersetting) {
      if (!setting.settingname || !setting.settingvalue) {
        continue; // Skip invalid settings
      }
      
      try {
        const rawJson =
          setting.jsondata ??
          (setting as Record<string, unknown>)['JsonData'] ??
          (setting as Record<string, unknown>)['jsonData'];
        const decryptedSetting = {
          settingname: await this.encryptionService.decryptAes256Gcm(setting.settingname),
          settingvalue: await this.encryptionService.decryptAes256Gcm(setting.settingvalue),
          tenantid: setting.tenantid ? await this.encryptionService.decryptAes256Gcm(setting.tenantid) : '',
          id: setting.id || '',
          // API may send json on jsondata, JsonData, or as a JSON string; normalize in buildRequestTypeLabelMap
          jsondata: rawJson != null && rawJson !== '' ? rawJson : null
        };
        
        decryptedSettings.push(decryptedSetting);
      } catch (e) {
        // Skip this setting if decryption fails
      }
    }
    
    return decryptedSettings;
  }

  // Store login `dm_email` for appointment confirmation toperson
  private persistDmEmailForUser(userInfo: { dmEmail?: string } | undefined): void {
    persistDmEmail(this.storageService, userInfo?.dmEmail);
  }

  /**
   * PE-6224 — flush login MFA audits after PortalSendIP for direct patients.
   * Representatives flush on associated-patients select (selected PatientUID).
   */
  private flushPendingMfaAuditEvents(patientId: string, tenantId: string): void {
    const events = this.storageService.getSessionStorage(MFA_PENDING_AUDIT_EVENTS_KEY);
    this.storageService.removeSessionStorageByKey(MFA_PENDING_AUDIT_EVENTS_KEY);

    if (!Array.isArray(events) || events.length === 0) {
      return;
    }

    const tenantid = String(tenantId ?? '').trim();
    const nPatientID = this.normalizePatientIdForAudit(patientId);
    if (!tenantid || !nPatientID) {
      return;
    }

    for (const event of events as PendingMfaAuditEvent[]) {
      if (!event?.sActivityType || !event?.sDescription) {
        continue;
      }
      this.apiService
        .insertPatientPortalAuditLog({
          tenantid,
          nPatientID,
          sActivityModule: 'Account',
          sActivityCategory: 'MFA',
          sActivityType: event.sActivityType,
          sDescription: event.sDescription,
        })
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
  }

  private normalizePatientIdForAudit(patientId: string): string {
    const trimmed = (patientId ?? '').trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
      return '';
    }
    return trimmed.split('-')[0].trim();
  }
}